import React, { useState } from 'react';
import { login } from './api';

const AdminLogin = ({ onLogin }) => {
  const [form,     setForm]     = useState({ username: '', password: '' });
  const [errors,   setErrors]   = useState({});
  const [touched,  setTouched]  = useState({});
  const [apiError, setApiError] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [showPass, setShowPass] = useState(false);

  /* ── Validation ── */
  function validate(f) {
    const e = {};
    if (!f.username.trim())           e.username = 'Username is required.';
    else if (f.username.trim().length < 2) e.username = 'Username must be at least 2 characters.';
    else if (f.username.trim().length > 50) e.username = 'Username cannot exceed 50 characters.';
    else if (/[<>"'&]/.test(f.username)) e.username = 'Username contains invalid characters.';

    if (!f.password)              e.password = 'Password is required.';
    else if (f.password.length < 4) e.password = 'Password must be at least 4 characters.';

    return e;
  }

  const touch = (field) => {
    setTouched(t => ({ ...t, [field]: true }));
    setErrors(validate(form));
  };

  const update = (field, value) => {
    const next = { ...form, [field]: value };
    setForm(next);
    setApiError('');
    if (touched[field]) setErrors(validate(next));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ username: true, password: true });
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setLoading(true);
    setApiError('');
    try {
      const data = await login(form.username.trim(), form.password);
      if (data.success) {
        localStorage.setItem('saanvi_admin_token', data.token);
        localStorage.setItem('saanvi_admin_user', data.username);
        onLogin(data.username);
      } else {
        setApiError(data.message || 'Invalid credentials. Please try again.');
      }
    } catch {
      setApiError('Unable to connect. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (field) => ({
    width: '100%', padding: '11px 14px',
    border: `1.5px solid ${touched[field] && errors[field] ? '#dc2626' : touched[field] ? '#16a34a' : '#dee2e6'}`,
    borderRadius: 8, fontSize: '0.9rem', outline: 'none',
    background: touched[field] && errors[field] ? '#fff5f5' : 'white',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxShadow: touched[field] && errors[field] ? '0 0 0 3px rgba(220,38,38,0.08)' : 'none',
    fontFamily: 'inherit',
  });

  return (
    <div className="admin-login-page">
      <div className="admin-login-card">
        <div className="admin-login-logo">
          <img
            src="/images/logo.jpeg"
            alt="Saanvi Innovations"
            style={{ height:56, width:'auto', maxWidth:220, objectFit:'contain', marginBottom:12, display:'block', margin:'0 auto 12px' }}
            onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
          />
          <div className="icon" style={{ display:'none' }}>S</div>
          <h2>Saanvi Admin</h2>
          <p>Sign in to manage your website</p>
        </div>

        {apiError && (
          <div style={{
            background:'#fef2f2', border:'1px solid #fecaca', color:'#991b1b',
            borderRadius:8, padding:'11px 14px', marginBottom:16, fontSize:'0.85rem',
            display:'flex', alignItems:'flex-start', gap:8,
          }}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" style={{flexShrink:0,marginTop:1}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {apiError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Username */}
          <div className="admin-form-group">
            <label style={{ fontSize:'0.83rem', fontWeight:600, color:'#374151', marginBottom:6, display:'block' }}>
              Username
            </label>
            <input
              type="text"
              placeholder="admin"
              value={form.username}
              style={inputStyle('username')}
              autoComplete="username"
              maxLength={50}
              autoFocus
              onChange={e => update('username', e.target.value)}
              onBlur={() => touch('username')}
            />
            {touched.username && errors.username && (
              <span style={{ display:'flex', alignItems:'center', gap:4, color:'#dc2626', fontSize:'0.78rem', marginTop:5, fontWeight:500 }}>
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {errors.username}
              </span>
            )}
          </div>

          {/* Password */}
          <div className="admin-form-group">
            <label style={{ fontSize:'0.83rem', fontWeight:600, color:'#374151', marginBottom:6, display:'block' }}>
              Password
            </label>
            <div style={{ position:'relative' }}>
              <input
                type={showPass ? 'text' : 'password'}
                placeholder="••••••••"
                value={form.password}
                style={{ ...inputStyle('password'), paddingRight: 44 }}
                autoComplete="current-password"
                onChange={e => update('password', e.target.value)}
                onBlur={() => touch('password')}
              />
              <button
                type="button"
                onClick={() => setShowPass(v => !v)}
                style={{
                  position:'absolute', right:12, top:'50%', transform:'translateY(-50%)',
                  background:'none', border:'none', cursor:'pointer', color:'#6b7280', padding:0,
                  display:'flex', alignItems:'center',
                }}
                aria-label={showPass ? 'Hide password' : 'Show password'}
              >
                {showPass
                  ? <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  : <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                }
              </button>
            </div>
            {touched.password && errors.password && (
              <span style={{ display:'flex', alignItems:'center', gap:4, color:'#dc2626', fontSize:'0.78rem', marginTop:5, fontWeight:500 }}>
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {errors.password}
              </span>
            )}
          </div>

          <button
            type="submit"
            className="btn-admin-primary"
            disabled={loading}
            style={{ width:'100%', justifyContent:'center', padding:'12px', marginTop:8, fontSize:'0.92rem', display:'flex', alignItems:'center', gap:8 }}
          >
            {loading ? (
              <>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="white" strokeWidth="2" style={{animation:'spin 0.7s linear infinite'}}><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>
                Signing in...
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="white" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                Sign In
              </>
            )}
          </button>
        </form>

        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
        <p style={{ textAlign:'center', marginTop:20, fontSize:'0.75rem', color:'#9ca3af' }}>
          Saanvi Innovations Admin Panel v1.0
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
